// Generic entity accessors — the client half of the entity registry.
//
// Paths, defaults, and writable fields all come from entityRegistry.gen.json
// (generated from backend/shared/entities/registry.js — run
// `node scripts/generate-entities.mjs` after registry edits; never edit the
// JSON by hand). Adding an entity to the registry makes it usable here with
// zero new client code:
//
//   const { docs: assets } = useEntityList('asset', { item: itemId });
//   await createEntity('asset', { item: itemId }, { name, s3Key });
//
// Scope objects name ancestor ids by entity: { item: 'i1' }.
//
// The hand-written demo accessors in data/firestore.js still exist (they
// carry demo-specific logic like counter increments); NEW domains should
// start here and only hand-write what the registry can't express.
import { createContext, useContext, useEffect, useState } from 'react';
import firebase, { db } from '../firebaseConfig';
import registry from './entityRegistry.gen.json';
import { isPg, subscribeRows, subscribeRow, getSupabase } from './supabaseClient';
import { resolvePath } from './pathMap';
import { pgInsert, pgSetMerge, pgDelete } from './firestore';
import { tenantStamp } from './tenantStamp';

const ready = () => (isPg() ? true : firebase.apps.length > 0 && db);

export const ENTITIES = registry.entities;
export const ACCESS_ALLOWLIST = registry.accessAllowlist;
export const ROLES = registry.roles || ['viewer', 'member', 'admin', 'owner'];

// ---- Tenancy (org → workspace → project) --------------------------------------
// TenantProvider (TenantContext.js) publishes the current org + workspace +
// the viewer's seats. Tenant-scoped entities (scope 'tenant': flat docs
// carrying orgId/workspaceId) are listed per visible workspace and stamped
// with the current tenant on create, so the rules' tenantOk() both lets the
// list through and keeps a workspace viewer out of another workspace.
export const TenantContext = createContext({
  orgId: null, role: null, workspace: null, workspaceIds: [], wsRoles: {}, workspaces: [], orgs: [],
  setWorkspace: () => {}, setOrg: () => {},
});
export const useTenant = () => useContext(TenantContext);
export const isPrivileged = (role) => role === 'admin' || role === 'owner';
export const roleSatisfies = (held, min) => ROLES.indexOf(held) >= 0 && ROLES.indexOf(held) >= ROLES.indexOf(min);

export { setCurrentTenant, getCurrentTenant, tenantStamp } from './tenantStamp';

// Role the viewer holds over a workspace (org admin/owner pass everywhere).
export function workspaceRole(orgRole, wsRoles, wsId) {
  if (isPrivileged(orgRole)) return orgRole;
  if (!wsId) return orgRole;
  return wsRoles?.[wsId] || null;
}

// Workspace buckets a tenant-scoped list must query. null = org-wide only
// filter (privileged, no workspace selected); [] = nothing visible.
function tenantBuckets(tenant) {
  if (!tenant?.orgId) return undefined; // no tenancy yet → legacy open list
  if (tenant.workspace) return [tenant.workspace];
  if (isPrivileged(tenant.role)) return null;
  return [null, ...(tenant.workspaceIds || []).slice(0, 20)];
}

function parentChain(entityName) {
  const chain = [];
  let cur = ENTITIES[entityName]?.parent;
  while (cur) {
    chain.unshift(cur);
    cur = ENTITIES[cur]?.parent;
  }
  return chain;
}

export function collectionPath(entityName, scope = {}) {
  const def = ENTITIES[entityName];
  if (!def) throw new Error(`unknown entity: ${entityName}`);
  const segs = [];
  for (const anc of parentChain(entityName)) {
    if (!scope[anc]) throw new Error(`missing scope id for '${anc}' (entity ${entityName})`);
    segs.push(ENTITIES[anc].collection, String(scope[anc]));
  }
  segs.push(def.collection);
  return segs.join('/');
}

export const docPath = (entityName, scope, id) => `${collectionPath(entityName, scope)}/${id}`;

// Strip server-only keys and apply declared defaults for a create payload.
function prepareCreate(entityName, payload) {
  const def = ENTITIES[entityName];
  const out = {};
  for (const [key, f] of Object.entries(def.fields)) {
    if (f.serverOnly) continue;
    if (payload[key] !== undefined && payload[key] !== null) out[key] = payload[key];
    else if (f.default !== undefined) out[key] = f.default;
  }
  if (def.fields.createdAt) out.createdAt = out.createdAt || Date.now();
  // Tenant-scoped entities are stamped with the current org/workspace unless
  // the caller chose explicitly (null workspaceId = org-wide).
  if (def.scope === 'tenant' && out.orgId == null) Object.assign(out, tenantStamp());
  return out;
}

const sortDocs = (arr, orderBy, dir) => {
  if (!orderBy) return arr;
  return arr.sort((a, b) => {
    const av = a[orderBy], bv = b[orderBy];
    if (av === bv) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    const r = av > bv ? 1 : -1;
    return dir === 'desc' ? -r : r;
  });
};

// ---- Live hooks ---------------------------------------------------------------
// Tenant-scoped entities read the TenantContext and fan out one equality
// query per visible workspace bucket (orgId == org && workspaceId == bucket),
// merged + sorted client-side — equality-only, so no composite index.
export function useEntityList(entityName, scope, { orderBy = 'createdAt', dir = 'desc', enabled = true } = {}) {
  const [docs, setDocs] = useState(null);
  const [error, setError] = useState(null);
  const scopeKey = JSON.stringify(scope || {});
  const tenant = useTenant();
  const tenantScoped = ENTITIES[entityName]?.scope === 'tenant';
  const buckets = tenantScoped ? tenantBuckets(tenant) : undefined;
  const tenantKey = tenantScoped ? JSON.stringify([tenant.orgId, buckets]) : '';

  useEffect(() => {
    if (!ready() || !enabled) {
      setDocs(null);
      return undefined;
    }
    let path;
    try {
      path = collectionPath(entityName, JSON.parse(scopeKey));
    } catch {
      setDocs(null); // scope incomplete (e.g. still loading an ancestor id)
      return undefined;
    }
    const orgId = tenantScoped ? tenant.orgId : null;
    if (tenantScoped && buckets && buckets.length === 0) {
      setDocs([]);
      return undefined;
    }
    if (isPg()) {
      let resolved;
      try {
        resolved = resolvePath(path);
      } catch (e) {
        setError(e.message);
        setDocs([]);
        return undefined;
      }
      const match = !orgId ? undefined
        : (d) => d.orgId === orgId && (buckets === null || buckets.includes(d.workspaceId ?? null));
      return subscribeRows(resolved.table, resolved.filters, { orderBy, dir, match }, setDocs, (msg) => {
        setError(msg);
        setDocs([]);
      });
    }
    const onErr = (err) => {
      setError(err.message);
      setDocs([]);
    };
    if (!orgId) {
      let ref = db.collection(path);
      if (orderBy) ref = ref.orderBy(orderBy, dir);
      const unsub = ref.onSnapshot((snap) => setDocs(snap.docs.map((d) => ({ id: d.id, ...d.data() }))), onErr);
      return () => unsub();
    }
    const base = db.collection(path).where('orgId', '==', orgId);
    const queries = buckets === null ? [base] : buckets.map((b) => base.where('workspaceId', '==', b));
    const parts = queries.map(() => null);
    const emit = () => {
      if (parts.some((p) => p === null)) return;
      const merged = new Map();
      for (const p of parts) for (const d of p) merged.set(d.id, d);
      setDocs(sortDocs(Array.from(merged.values()), orderBy, dir));
    };
    const unsubs = queries.map((q, i) => q.onSnapshot((snap) => {
      parts[i] = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      emit();
    }, onErr));
    return () => unsubs.forEach((u) => u());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityName, scopeKey, orderBy, dir, enabled, tenantKey]);

  return { docs, error };
}

// The viewer's own seats: orgs (members) and workspaces (wsMembers), live.
// Path shape: orgs/{o}/members/{uid} · orgs/{o}/workspaces/{w}/wsMembers/{uid}.
export function useMySeats(uid) {
  const [orgs, setOrgs] = useState({});
  const [ws, setWs] = useState({});
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (!ready() || !uid) { setOrgs({}); setWs({}); setLoaded(!uid); return undefined; }
    let n = 0;
    const done = () => { n += 1; if (n >= 2) setLoaded(true); };
    if (isPg()) {
      // Seat rows key on the parent-id columns (not in data) → read them
      // directly; polled, since seats change rarely.
      const sb = getSupabase();
      let alive = true;
      const load = async () => {
        if (!sb) return;
        const [m, w] = await Promise.all([
          sb.from('org_members').select('id,org_id,data').eq('id', uid),
          sb.from('workspace_ws_members').select('id,org_id,workspace_id,data').eq('id', uid),
        ]);
        if (!alive) return;
        setOrgs(Object.fromEntries((m.data || []).map((r) => [r.org_id, r.data?.role])));
        setWs(Object.fromEntries((w.data || []).map((r) => [`${r.org_id}/${r.workspace_id}`, r.data?.role])));
        setLoaded(true);
      };
      load();
      const t = setInterval(load, 30000);
      return () => { alive = false; clearInterval(t); };
    }
    const u1 = db.collectionGroup('members').where('uid', '==', uid).onSnapshot((snap) => {
      const out = {};
      snap.docs.forEach((d) => { const s = d.ref.path.split('/'); if (s[0] === 'orgs') out[s[1]] = d.data().role; });
      setOrgs(out);
      done();
    }, () => done());
    const u2 = db.collectionGroup('wsMembers').where('uid', '==', uid).onSnapshot((snap) => {
      const out = {};
      snap.docs.forEach((d) => { const s = d.ref.path.split('/'); if (s[0] === 'orgs' && s[2] === 'workspaces') out[`${s[1]}/${s[3]}`] = d.data().role; });
      setWs(out);
      done();
    }, () => done());
    return () => { u1(); u2(); };
  }, [uid]);
  return { orgs, wsRoles: ws, loaded };
}

export function useEntityDoc(entityName, scope, id) {
  const [doc, setDoc] = useState(undefined);
  const [error, setError] = useState(null);
  const scopeKey = JSON.stringify(scope || {});

  useEffect(() => {
    if (!ready() || !id) {
      setDoc(undefined);
      return undefined;
    }
    let path;
    try {
      path = docPath(entityName, JSON.parse(scopeKey), id);
    } catch {
      setDoc(undefined);
      return undefined;
    }
    if (isPg()) {
      let resolved;
      try {
        resolved = resolvePath(path);
      } catch (e) {
        setError(e.message);
        return undefined;
      }
      return subscribeRow(resolved.table, resolved.id, setDoc, (msg) => setError(msg), resolved.filters);
    }
    const unsub = db.doc(path).onSnapshot(
      (snap) => setDoc(snap.exists ? { id: snap.id, ...snap.data() } : null),
      (err) => setError(err.message)
    );
    return () => unsub();
  }, [entityName, scopeKey, id]);

  return { doc, error };
}

// ---- CRUD -----------------------------------------------------------------------
export async function createEntity(entityName, scope, payload, { id = null } = {}) {
  if (isPg()) {
    const rid = id != null ? String(id) : crypto.randomUUID();
    await pgInsert(collectionPath(entityName, scope), rid, prepareCreate(entityName, payload));
    return rid;
  }
  const col = db.collection(collectionPath(entityName, scope));
  const ref = id ? col.doc(String(id)) : col.doc();
  await ref.set(prepareCreate(entityName, payload));
  return ref.id;
}

export async function updateEntity(entityName, scope, id, patch) {
  if (isPg()) {
    await pgSetMerge(docPath(entityName, scope, id), { ...patch, updatedAt: Date.now() });
    return;
  }
  await db.doc(docPath(entityName, scope, id)).set(
    { ...patch, updatedAt: Date.now() },
    { merge: true }
  );
}

export async function deleteEntity(entityName, scope, id) {
  if (isPg()) {
    await pgDelete(docPath(entityName, scope, id));
    return;
  }
  await db.doc(docPath(entityName, scope, id)).delete();
}
